import React from 'react';
import { motion } from 'framer-motion';
import { 
    Zap, Shield, Globe, Cpu, Layout, Users, 
    Calendar, Bell, BarChart3, Database, Lock, 
    ArrowRight, CheckCircle2, Search
} from 'lucide-react';
import PublicLayout from '../components/layout/PublicLayout';

const FeaturesPage = () => {
    const mainFeatures = [
        {
            icon: Calendar,
            title: "Dynamic Scheduling",
            description: "Advanced algorithms that handle complex constraints, faculty preferences, and room availability in real-time.",
            color: "blue"
        },
        {
            icon: Shield,
            title: "Enterprise Security",
            description: "Bank-grade encryption and granular role-based access control to keep your institutional data safe.",
            color: "indigo"
        },
        {
            icon: Globe,
            title: "Global Sync",
            description: "Seamless integration with Google Calendar, Outlook, and other major platforms for universal accessibility.",
            color: "cyan"
        },
        {
            icon: BarChart3,
            title: "Advanced Analytics",
            description: "Deep insights into room utilization, faculty workload, and scheduling efficiency with visual dashboards.",
            color: "violet"
        },
        {
            icon: Cpu,
            title: "AI-Powered Optimization",
            description: "Machine learning engines that predict conflicts before they happen and suggest optimal scheduling paths.",
            color: "emerald"
        },
        {
            icon: Users,
            title: "Collaborative Workflow",
            description: "Multi-user environment with approval workflows and real-time notifications for administrative teams.",
            color: "amber"
        }
    ];

    return (
        <PublicLayout>
            <div className="pt-40 pb-20 px-6">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-32"
                    >
                        <h1 className="text-4xl sm:text-5xl md:text-8xl font-black tracking-tighter mb-8 bg-gradient-to-b from-zinc-950 to-zinc-600 dark:from-white dark:to-white/40 bg-clip-text text-transparent leading-[1.1]">
                            Powerfully Simple. <br className="hidden sm:block" /> Built for Scale.
                        </h1>
                        <p className="text-neutral-600 dark:text-neutral-400 text-xl md:text-2xl max-w-3xl mx-auto font-medium leading-relaxed">
                            Our platform combines industry-leading algorithms with a world-class user experience to revolutionize academic administration.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-40">
                        {mainFeatures.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="group relative p-10 rounded-[2.5rem] bg-white/50 dark:bg-zinc-900/50 border border-neutral-200 dark:border-white/5 hover:border-blue-500/30 transition-all duration-500 shadow-sm dark:shadow-none"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-[2.5rem]"></div>
                                <div className={`w-16 h-16 rounded-2xl bg-${feature.color}-500/10 flex items-center justify-center mb-8 border border-${feature.color}-500/20`}>
                                    <feature.icon className={`w-8 h-8 text-${feature.color}-500 dark:text-${feature.color}-400`} />
                                </div>
                                <h3 className="text-2xl font-black text-zinc-900 dark:text-white mb-4">{feature.title}</h3>
                                <p className="text-neutral-600 dark:text-zinc-500 font-medium leading-relaxed mb-8">
                                    {feature.description}
                                </p>
                                <div className="flex items-center gap-2 text-sm font-black text-neutral-400 dark:text-white/40 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">
                                    Explore Feature <ArrowRight className="w-4 h-4" />
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Detailed Section */}
                    <div className="space-y-40 mb-40">
                        <div className="flex flex-col lg:flex-row items-center gap-20">
                            <div className="flex-1">
                                <span className="text-blue-500 font-black uppercase tracking-[0.3em] text-xs mb-6 block">Intelligence</span>
                                <h2 className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter text-zinc-900 dark:text-white mb-8">
                                    The Algorithm <br className="hidden sm:block" /> Behind the Magic.
                                </h2>
                                <p className="text-neutral-600 dark:text-zinc-500 text-lg md:text-xl font-medium leading-relaxed mb-10">
                                    Our proprietary 'Titan' scheduling engine processes millions of data points to generate conflict-free timetables in seconds, not hours.
                                </p>
                                <ul className="space-y-4">
                                    {["Multi-constrained optimization", "Faculty preference weightings", "Adaptive learning feedback loops"].map((item, i) => (
                                        <li key={i} className="flex items-center gap-4 text-zinc-800 dark:text-white font-bold">
                                            <CheckCircle2 className="w-6 h-6 text-blue-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex-1 relative group">
                                <div className="absolute -inset-4 bg-blue-600 rounded-[3rem] blur-2xl opacity-10 group-hover:opacity-20 transition duration-1000"></div>
                                <div className="relative aspect-video rounded-[3rem] bg-white dark:bg-zinc-900 border border-neutral-200 dark:border-white/5 overflow-hidden shadow-2xl">
                                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 to-transparent"></div>
                                    <div className="absolute top-8 left-8 right-8 bottom-8 flex flex-col justify-between">
                                        <div className="flex items-center justify-between">
                                            <div className="flex gap-2">
                                                <div className="w-3 h-3 rounded-full bg-red-500/20 border border-red-500/40"></div>
                                                <div className="w-3 h-3 rounded-full bg-amber-500/20 border border-amber-500/40"></div>
                                                <div className="w-3 h-3 rounded-full bg-emerald-500/20 border border-emerald-500/40"></div>
                                            </div>
                                            <div className="px-4 py-1.5 rounded-full bg-neutral-100 dark:bg-white/5 border border-neutral-200 dark:border-white/10 text-[10px] font-black uppercase tracking-widest text-zinc-500">Titan Engine</div>
                                        </div>
                                        <div className="flex-1 flex items-center justify-center">
                                            <div className="w-full max-w-md space-y-4">
                                                {[80, 60, 90, 40].map((w, i) => (
                                                    <div key={i} className="h-2 rounded-full bg-neutral-100 dark:bg-zinc-800 overflow-hidden relative">
                                                        <motion.div 
                                                            initial={{ width: 0 }}
                                                            whileInView={{ width: `${w}%` }}
                                                            className="absolute inset-0 bg-blue-600"
                                                        ></motion.div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col lg:flex-row-reverse items-center gap-20">
                            <div className="flex-1">
                                <span className="text-violet-500 font-black uppercase tracking-[0.3em] text-xs mb-6 block">User Experience</span>
                                <h2 className="text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter text-zinc-900 dark:text-white mb-8">
                                    Design That <br className="hidden sm:block" /> Disappears.
                                </h2>
                                <p className="text-neutral-600 dark:text-zinc-500 text-lg md:text-xl font-medium leading-relaxed mb-10">
                                    We believe that professional tools shouldn't feel like a chore. The platform is designed to be intuitive, fast, and surprisingly beautiful.
                                </p>
                                <ul className="space-y-4">
                                    {["Dynamic dark & light modes", "60fps buttery animations", "Instant search & keyboard shortcuts"].map((item, i) => (
                                        <li key={i} className="flex items-center gap-4 text-zinc-800 dark:text-white font-bold">
                                            <CheckCircle2 className="w-6 h-6 text-violet-500" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex-1 relative group">
                                <div className="absolute -inset-4 bg-violet-600 rounded-[3rem] blur-2xl opacity-10 group-hover:opacity-20 transition duration-1000"></div>
                                <div className="relative aspect-square max-w-md mx-auto rounded-[3rem] bg-white dark:bg-zinc-900 border border-neutral-200 dark:border-white/5 overflow-hidden shadow-2xl p-12">
                                    <div className="grid grid-cols-2 gap-6 h-full">
                                        {[Layout, Users, Calendar, Database].map((Icon, i) => (
                                            <div key={i} className="rounded-3xl bg-neutral-50 dark:bg-zinc-800/50 border border-neutral-200 dark:border-white/5 flex items-center justify-center p-8 hover:bg-neutral-100 dark:hover:bg-zinc-800 transition-all">
                                                <Icon className="w-12 h-12 text-neutral-400 dark:text-zinc-500 group-hover:text-violet-500 dark:group-hover:text-violet-400 transition-colors" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default FeaturesPage;
